import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Arquivo, FiltroArquivo } from '@pgcustom/shared-types';

/**
 * Subconjunto de `FiltroArquivo` sem `tags`/`incluirExcluidos` —
 * `ApiClient.get` só aceita valores escalares na querystring
 * (`ParametrosConsulta`), e a tela de Galeria ainda não tem um filtro
 * dedicado de tags (a busca por nome já cobre o caso de uso principal;
 * tags aparecem como metadado no card e são editáveis no modal de
 * detalhe). Adicionar filtro por tag no futuro exigiria decidir a
 * codificação de múltiplos valores na querystring — não inventado aqui
 * sem necessidade real.
 */
export type FiltroGaleria = Pick<FiltroArquivo, 'module' | 'folder' | 'query' | 'isPublic'>;

/** Campos aceitos por `PATCH /storage/:id` (`atualizarMetadadosSchema`, `folder: null` limpa o campo). */
export interface AtualizarMetadadosInput {
  tags?: string[];
  folder?: string | null;
  isPublic?: boolean;
}

export interface NovoUploadInput {
  arquivo: File;
  module: string;
  folder?: string;
  tags?: string[];
  isPublic?: boolean;
}

/**
 * Adapter do módulo Galeria no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/storage/*`
 * (`apps/api/src/modules/storage/storage.routes.ts`). Sprint 12 é
 * inteiramente frontend: o backend do Storage já entrega busca/filtro/
 * tags/módulo desde a Sprint 1, nenhum endpoint novo foi necessário
 * (`ROADMAP.md`, Sprint 12: "sem nova lógica de backend além do
 * necessário para busca/filtro" — e essa lógica já existia).
 */
export class GaleriaApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroGaleria = {}): Promise<Resultado<Arquivo[]>> {
    try {
      const resposta = await this.api.get<{ arquivos: Arquivo[] }>('/storage', { ...filtro });
      if (resposta.arquivos.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.arquivos };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar arquivos' };
    }
  }

  async upload(dado: NovoUploadInput): Promise<Arquivo> {
    const formData = new FormData();
    formData.set('file', dado.arquivo);
    formData.set('module', dado.module);
    if (dado.folder) formData.set('folder', dado.folder);
    if (dado.tags && dado.tags.length > 0) formData.set('tags', JSON.stringify(dado.tags));
    if (dado.isPublic !== undefined) formData.set('isPublic', String(dado.isPublic));

    const resposta = await this.api.upload<{ arquivo: Arquivo; url: string }>('/storage', formData);
    return resposta.arquivo;
  }

  async atualizarMetadados(id: string, dado: AtualizarMetadadosInput): Promise<Arquivo> {
    const resposta = await this.api.patch<{ arquivo: Arquivo }>(`/storage/${id}`, dado);
    return resposta.arquivo;
  }

  async excluir(id: string): Promise<void> {
    await this.api.excluir(`/storage/${id}`);
  }

  /** URL de download/preview — cookie de sessão já autentica automaticamente (`<img src>`/link direto, mesma origem). */
  urlDownload(id: string): string {
    return `/api/v1/storage/${id}/download`;
  }
}
